import { camelize } from 'humps';

import { assessmentTx } from '../i18n/assessmentTx';
import {
  OTHER_CHOICE_VALUE,
  type AssessmentQuestion,
  type RepeatingListColumn,
  type RepeatingListRow,
} from '../types/assessmentQuestion';

// A SurveyJS element as returned in `DynamicUiPayload.elements`.
// Typed loosely — only the fields we actually consume are required.
// Choice values are numbers on scale questions (PHQ/GAD send 0–3) and strings
// elsewhere ('yes', 'type_1', …).
type SurveyJsChoice =
  | string
  | {
      value?: string | number;
      text?: string;
      imageLink?: string;
      exclusive?: boolean;
      /** Per-choice helper text, shown under the row only while selected. See `QuestionChoice.description`. */
      description?: string;
    };

// A `matrixdynamic` column, authored in the survey builder as
// `{ name, title, cellType, min, max }`. `cellType` selects the open-ended
// shapes only (text/date/number, same set `AssessmentQuestion.questionType`
// covers for a standalone question) — a choice-based `cellType`
// (dropdown/checkbox/radiogroup/boolean/comment) falls back to `'text'`, see
// `RepeatingListColumn`'s doc comment. `min`/`max` are only read when they
// match the resolved cell type (string for `'date'`, number for `'number'`),
// mirroring how a standalone question's bounds are resolved below.
//
// SurveyJS also accepts a string shorthand — `columns: ["Medication", "Dosage"]`
// — where the string serves as both `name` and `title`, same as `SurveyJsChoice`
// above. Without this arm, a shorthand-authored column's `name` reads as
// `undefined` (a string has no `.name`), `isNamedColumn` drops it, and a
// matrixdynamic authored entirely with shorthand columns ends up with an empty
// `columns` array — the same "no fields to fill" dead-end `AssessmentContent`
// guards against for `columns: []` directly.
type SurveyJsColumn =
  | string
  | {
      name?: string;
      title?: string;
      cellType?: string;
      min?: string | number;
      max?: string | number;
    };

const REPEATING_LIST_CELL_TYPES = new Set(['text', 'date', 'number']);

// Authored `min`/`max` for a numeric bound, accepted as either shape
// (`min: 50` or `min: "50"`) — no evidence today's payloads author the
// string form, but `Number(...)` + `Number.isFinite` covers it for free
// wherever a plain `typeof === 'number'` check would otherwise silently drop
// it. `undefined` covers "absent" as well as "authored but not a real
// number" (e.g. an empty string or "today").
function toFiniteNumber(v: string | number | undefined): number | undefined {
  // Number('') is 0, not NaN — guarded separately so a blank string reads as
  // "no bound authored", not a floor/ceiling of zero.
  if (v === undefined || (typeof v === 'string' && v.trim() === '')) return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
}

// Reads the column's OWN `cellType` — not a column-level `inputType`, the
// shape vanilla SurveyJS docs describe for an authored date/number matrix
// cell (`{ cellType: "text", inputType: "number" }`). Confirmed against both
// of today's producers: the webserver generator's `_column()` (see
// `generate_pre_consult_survey_flow.py`) sets `cellType` to `'date'`/`'number'`
// directly, and the builder's matrixdynamic column editor (`MatrixForm`) has
// no per-column `inputType` field at all — only `cellType`. If a future
// producer starts authoring the vanilla-SurveyJS shape instead, this needs a
// second `inputType` read alongside `cellType` below.
function resolveCellType(raw: unknown): 'text' | 'date' | 'number' {
  return typeof raw === 'string' && REPEATING_LIST_CELL_TYPES.has(raw)
    ? (raw as 'text' | 'date' | 'number')
    : 'text';
}

type SurveyJsElement = {
  type: string;
  name: string;
  title?: string;
  /** SurveyJS helper text under the title (the legacy screeners' `additional_info`). */
  description?: string;
  /** Present on `text` elements: `"date"`, `"email"`, etc. */
  inputType?: string;
  /** Inclusive ISO `YYYY-MM-DD` floor on date elements, when authored. Numeric on number inputs. */
  min?: string | number;
  /** Inclusive ISO `YYYY-MM-DD` ceiling on date elements, when authored. Numeric on number inputs. */
  max?: string | number;
  choices?: SurveyJsChoice[];
  /** SurveyJS required flag. Explicitly false → the question is skippable. */
  isRequired?: boolean;
  /** Present on `matrixdynamic` elements. */
  columns?: SurveyJsColumn[];
  /** Present on `matrixdynamic` elements: the builder's authored starting row count. */
  rowCount?: number;
  /** Inline Other free-text on a checkbox (SurveyJS showOtherItem). */
  showOtherItem?: boolean;
  otherText?: string;
  otherPlaceholder?: string;
  isOtherRequired?: boolean;
};

function isNamedColumn(c: SurveyJsColumn): c is
  | string
  | {
      name: string;
      title?: string;
      cellType?: string;
      min?: string | number;
      max?: string | number;
    } {
  return typeof c === 'string' ? c.length > 0 : typeof c.name === 'string' && c.name.length > 0;
}

function toRepeatingListColumns(columns: SurveyJsColumn[] | undefined): RepeatingListColumn[] {
  return (columns ?? []).filter(isNamedColumn).map((c) => {
    if (typeof c === 'string') return { name: c, title: c, cellType: 'text' };

    const cellType = resolveCellType(c.cellType);
    const minDate = cellType === 'date' && typeof c.min === 'string' && c.min ? c.min : undefined;
    const maxDate = cellType === 'date' && typeof c.max === 'string' && c.max ? c.max : undefined;
    const minNumber = cellType === 'number' ? toFiniteNumber(c.min) : undefined;
    const maxNumber = cellType === 'number' ? toFiniteNumber(c.max) : undefined;

    return {
      name: c.name,
      title: c.title ?? c.name,
      cellType,
      ...(minDate !== undefined ? { minDate } : {}),
      ...(maxDate !== undefined ? { maxDate } : {}),
      ...(minNumber !== undefined ? { minNumber } : {}),
      ...(maxNumber !== undefined ? { maxNumber } : {}),
    };
  });
}

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Every spelling one authored `prefilledAnswers` key can arrive in, mapped
 * back to the authored name.
 *
 * `prefilledAnswers` is the one place on this wire where the KEYS are data,
 * not protocol: they are a `matrixdynamic` element's authored `name` and,
 * inside each row, its authored column names — `current_weight_loss_medicine_list`,
 * `medicine_name`. Both hosts run their JSON responses through
 * `camelizeKeys`, which recurses into exactly those keys and rewrites them
 * (`medicine_name` → `medicineName`), so a lookup by the authored name alone
 * finds nothing and every prefilled row is silently dropped. (The request
 * side already dodges this: `transitionSession` deliberately keeps `answers`
 * out of `decamelizeKeys` for the same reason.)
 *
 * Normalizing here rather than preserving the raw body upstream is what makes
 * this host-agnostic: onboarding's own `apiClient` camelizes at the injection
 * boundary (`normalizeClient`) before `asSession` ever sees the response, so
 * there is no raw copy left to read — whereas VidaReactNative's injected
 * instance does not. Both spellings are accepted instead of either being
 * assumed. Camelizing our OWN authored names to build the lookup is lossless
 * in that direction; decamelizing the host's keys back would not be.
 *
 * The authored spelling is inserted last so it wins if some column is
 * authored as the camelCase twin of another (`medicine_name` +
 * `medicineName`).
 */
function byAuthoredOrCamelized(names: string[]): Map<string, string> {
  const lookup = new Map<string, string>();
  for (const name of names) lookup.set(camelize(name), name);
  for (const name of names) lookup.set(name, name);
  return lookup;
}

/**
 * Validates/sanitizes one `matrixdynamic` element's slice of the untrusted
 * `prefilledAnswers` payload into `AssessmentQuestion.initialRows`.
 *
 * `raw` is expected to be an array of plain objects (column name → typed
 * string), one per starting row — but nothing on the wire guarantees that
 * shape, so this never throws on a mismatch:
 *   - Not an array (missing key, `null`, an object, a string, …) → `undefined`
 *     (caller falls back to `initialRowCount` blank rows).
 *   - An array entry that isn't a plain object (a string, a number, `null`,
 *     a nested array) is DROPPED — it doesn't invalidate the other entries,
 *     it just doesn't become a row.
 *   - Within a surviving row object, a key that names none of this element's
 *     OWN columns is dropped (an authored payload can't introduce a column
 *     `RepeatingListField` doesn't know how to render), and a value that
 *     isn't a string is dropped (the row survives, just without that cell —
 *     `seedRows` fills the gap with `''` from its blank column map). A key is
 *     matched in either spelling a host's response normalizer can deliver it
 *     in, and stored back under the AUTHORED column name — see
 *     `byAuthoredOrCamelized`.
 *   - A row object none of whose keys survived stays a row, with no values:
 *     the server said "this many rows", and an all-blank row is what
 *     `seedRows` would have rendered for it anyway.
 *   - Only when the array is empty, or held no plain objects at all, does this
 *     return `undefined` rather than `[]` — an empty `initialRows` reads the
 *     same as "no prefilled answers" to `seedRows`, so this keeps the two
 *     indistinct at the source instead of every caller re-checking `.length`.
 */
function toInitialRows(
  raw: unknown,
  columns: RepeatingListColumn[]
): RepeatingListRow[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const columnByKey = byAuthoredOrCamelized(columns.map((c) => c.name));
  const rows = raw.filter(isPlainRecord).map((row) => {
    const values: RepeatingListRow = {};
    for (const [key, value] of Object.entries(row)) {
      const column = columnByKey.get(key);
      if (column !== undefined && typeof value === 'string') values[column] = value;
    }
    return values;
  });
  return rows.length > 0 ? rows : undefined;
}

// The builder's own row-count input only accepts whole numbers, so a
// fractional `rowCount` only reaches here through a hand-edited flow JSON
// (like the ones this module's fixtures/tests author directly). Left
// unrounded, it would silently truncate wherever it lands — spread into an
// array literal, or `Array.from({ length })` in `AssessmentContent` — rather
// than failing loudly, and `Infinity` would hang/crash that same call instead
// of falling back. Rounds a finite positive value to honor
// `AssessmentQuestion.initialRowCount`'s "guaranteed positive integer"
// contract; anything else (absent, non-numeric, non-finite, non-positive)
// falls back to 1, same as before.
//
// The outer `Math.max(1, …)` matters on its own: `rowCount` values below 0.5
// (e.g. 0.4) pass the `> 0` guard above but `Math.round` alone would still
// return 0, silently breaking the same "positive integer" contract this
// function exists to uphold — `AssessmentContent` trusts it and doesn't
// re-clamp, so a 0 would reach `Array.from({ length: 0 })` and render a
// required question with no fields and no way to know why.
function toInitialRowCount(rowCount: number | undefined): number {
  if (typeof rowCount !== 'number' || !Number.isFinite(rowCount) || rowCount <= 0) return 1;
  return Math.max(1, Math.round(rowCount));
}

function isSurveyJsElement(v: unknown): v is SurveyJsElement {
  return typeof v === 'object' && v !== null && typeof (v as SurveyJsElement).name === 'string';
}

/**
 * Stringify a choice value, preserving `0` (`String(0)` → `'0'`) — a plain
 * truthiness or `??` chain would drop it. `'0'` is a truthy string, so it also
 * survives `useAssessmentQuestions`' `surveyChoiceUrn ? urn : order` fallback,
 * which previously replaced value 0 with its order and both corrupted PHQ/GAD
 * scores and produced duplicate React keys against numeric value 1.
 * Stringifying is safe server-side: numeric answers are only consumed through
 * `int(value)` coercion (accumulators, phq_gad_scoring, legacy_sync), and
 * equality predicates only compare string answer values.
 */
function toValueString(v: string | number | undefined): string | undefined {
  return v === undefined ? undefined : String(v);
}

function resolveChoiceText(c: SurveyJsChoice): string {
  if (typeof c === 'string') return c;
  return c.text ?? toValueString(c.value) ?? '';
}

function resolveChoiceValue(c: SurveyJsChoice): string {
  if (typeof c === 'string') return c;
  return toValueString(c.value) ?? c.text ?? '';
}

function toOtherSignalKey(elementName: string): string {
  return `${elementName}_other`;
}

function mapChoices(choices: SurveyJsChoice[]) {
  return choices.map((c, choiceIndex) => ({
    text: resolveChoiceText(c),
    order: choiceIndex + 1,
    surveyChoiceUrn: resolveChoiceValue(c),
    imageLink: typeof c === 'string' ? undefined : c.imageLink,
    exclusive: typeof c === 'string' ? undefined : c.exclusive,
    description: typeof c === 'string' ? undefined : c.description,
  }));
}

/**
 * Converts the raw SurveyJS `elements` array from a `DynamicUiPayload` into the
 * `AssessmentQuestion[]` shape that `AssessmentContent` / `useAssessmentQuestions` consume.
 *
 * Key mapping:
 *   element.name          → question.key   (the signal key, e.g. `taking_aoms`)
 *   element.title         → question.text  (shown as the page heading)
 *   element.description   → question.additionalInfo (the heading subtitle — the
 *                           legacy screeners' `additional_info`: lab_preference's
 *                           in-network cost note, the AOM refill "best estimate"
 *                           reassurance, every pre-diabetes question's explainer)
 *   choice.value          → surveyChoiceUrn (used by useAssessmentQuestions as the
 *                           submitted answer value — no URN needed here;
 *                           numeric values are stringified, see toValueString)
 *   choice.imageLink      → imageLink       (optional per-choice icon URL, e.g.
 *                           health goals; rendered as a leading avatar when set)
 *   choice.exclusive      → exclusive       (mutually-exclusive checkbox choice,
 *                           e.g. health_conditions' "None of these" — selecting
 *                           it clears every other choice and vice versa; only
 *                           meaningful on multi-select questions)
 *   choice.description    → description     (optional per-choice helper text,
 *                           rendered by ChoiceField under the row only while
 *                           that choice is selected — see
 *                           QuestionChoice.description)
 *   type: "checkbox"      → isMultiSelect: true (AssessmentContent renders checkbox
 *                           rows + submits a string[]); radiogroup → single-select
 *   showOtherItem         → showOtherItem / otherKey / optional otherText,
 *                           otherPlaceholder, isOtherRequired (checkbox only;
 *                           injects a value "other" choice when missing)
 *   inputType: "date"     → questionType: "date" (AssessmentContent renders date input)
 *   min / max             → question.minDate / question.maxDate (ISO date
 *                           bounds; copied only for date questions, and only
 *                           when they are non-empty strings) OR
 *                           question.minNumber / question.maxNumber (copied
 *                           only for inputType "number" questions, and only
 *                           when they are numbers)
 *   type: "text"          → questionType: "text" (single-line free-text input),
 *                           unless inputType is "date" (checked first, above)
 *   inputType: "number"   → question.inputType: "number" (AssessmentContent
 *                           switches the on-screen keyboard to numeric and
 *                           validates the shape + minNumber/maxNumber on
 *                           Continue, e.g. goal weight / highest adult weight)
 *   inputType: "tel"      → question.inputType: "tel" (AssessmentContent
 *                           renders the masked PhoneField, e.g. doctor phone /
 *                           fax)
 *   type: "comment"       → questionType: "comment" (multiline free-text input)
 *   type: "matrixdynamic" → questionType: "repeatingList" (AssessmentContent
 *                           renders open-ended repeating rows with add/remove);
 *                           columns  → question.columns (name/title/cellType
 *                           tuples, carried through so the renderer knows
 *                           which fields to show per row and how to render/
 *                           validate each cell — see RepeatingListColumn);
 *                           rowCount → question.initialRowCount (the
 *                           renderer's STARTING row count, not a cap — ignored
 *                           when initialRows is present, see below)
 *   prefilledAnswers[name] → question.initialRows (server-supplied starting
 *                           row VALUES, validated/sanitized per element by
 *                           toInitialRows — dropped unknown column names,
 *                           dropped non-string values, dropped non-object
 *                           array entries, and normalized "not an array" /
 *                           "nothing survived validation" to `undefined` so
 *                           useRepeatingListRows.seedRows falls back to
 *                           initialRowCount blank rows unchanged; matrixdynamic
 *                           only)
 *   isRequired            → SurveyJS omits the flag when a question is
 *                           optional, so an absent flag resolves to `false`
 *                           here for every element type — the opposite of
 *                           the legacy default (see AssessmentQuestion.isRequired).
 *                           Every live production flow sets the flag
 *                           explicitly today, so this is currently
 *                           unreachable in practice, but it's the correct
 *                           SurveyJS-wide reading if a future flow omits it.
 */
export function toDynamicAssessmentQuestions(
  elements: unknown[],
  /** Page-level title fallback when the element has no own title (e.g. refill date). */
  pageTitle?: string | null,
  /** Page-level description fallback when the element has no own description. */
  pageDescription?: string | null,
  /**
   * Server-supplied starting rows for `matrixdynamic` elements, keyed by
   * element `name` (`DynamicUiPayload.prefilledAnswers`). Untrusted wire
   * data — each entry is validated/sanitized by `toInitialRows` before it
   * reaches `question.initialRows`. Absent on non-`matrixdynamic` questions
   * and on payloads from a server build that predates this field.
   */
  prefilledAnswers?: Readonly<Record<string, unknown>> | null
): AssessmentQuestion[] {
  return elements.filter(isSurveyJsElement).map((element, index) => {
    const questionType =
      element.type === 'matrixdynamic'
        ? 'repeatingList'
        : element.inputType === 'date'
          ? 'date'
          : element.type === 'text'
            ? 'text'
            : element.type === 'comment'
              ? 'comment'
              : 'choice';
    const isRepeatingList = questionType === 'repeatingList';
    const isDate = questionType === 'date';
    const isNumeric = questionType === 'text' && element.inputType === 'number';
    const minDate =
      isDate && typeof element.min === 'string' && element.min ? element.min : undefined;
    const maxDate =
      isDate && typeof element.max === 'string' && element.max ? element.max : undefined;
    const minNumber = isNumeric ? toFiniteNumber(element.min) : undefined;
    const maxNumber = isNumeric ? toFiniteNumber(element.max) : undefined;
    const showOtherItem = element.type === 'checkbox' && element.showOtherItem === true;
    const repeatingListColumns = isRepeatingList
      ? toRepeatingListColumns(element.columns)
      : undefined;
    let choices = mapChoices(element.choices ?? []);
    if (showOtherItem && !choices.some((c) => c.surveyChoiceUrn === OTHER_CHOICE_VALUE)) {
      choices = [
        ...choices,
        {
          text: element.otherText ?? assessmentTx.t('Other'),
          order: choices.length + 1,
          surveyChoiceUrn: OTHER_CHOICE_VALUE,
          imageLink: undefined,
          exclusive: undefined,
          description: undefined,
        },
      ];
    }

    return {
      key: element.name,
      order: index + 1,
      text: element.title ?? pageTitle ?? '',
      // Rendered as the ScreenHeading subtitle. `undefined` (not '') when neither
      // level carries one, so AssessmentContent omits the subtitle node entirely.
      additionalInfo: element.description ?? pageDescription ?? undefined,
      questionType,
      // Only relevant when questionType is 'text' (e.g. "number" for goal
      // weight / highest adult weight); AssessmentContent reads it to switch
      // the keyboard. Omitted for 'date', which already has its own input.
      inputType: questionType === 'text' ? element.inputType : undefined,
      ...(minDate ? { minDate } : {}),
      ...(maxDate ? { maxDate } : {}),
      ...(minNumber !== undefined ? { minNumber } : {}),
      ...(maxNumber !== undefined ? { maxNumber } : {}),
      // SurveyJS omits `isRequired` to mean optional, so an absent flag
      // resolves to `false` here for every element type — not just
      // text/comment. This differs from the legacy pass-through default
      // (absent → required), but that default belongs to a different
      // producer (`useAssignedScreener`, which never reaches this
      // converter): legacy screeners rely on it because they never send the
      // flag at all, whereas every SurveyJS flow either sets it explicitly
      // or means optional by omitting it.
      isRequired: element.isRequired === true,
      // SurveyJS `checkbox` → multi-select; `radiogroup` (and everything else) →
      // single-select. Drives checkbox-vs-radio rendering + array-vs-string answer.
      isMultiSelect: element.type === 'checkbox',
      choices,
      ...(showOtherItem
        ? {
            showOtherItem: true,
            otherKey: toOtherSignalKey(element.name),
            ...(element.otherText != null ? { otherText: element.otherText } : {}),
            ...(element.otherPlaceholder ? { otherPlaceholder: element.otherPlaceholder } : {}),
            ...(element.isOtherRequired === true ? { isOtherRequired: true } : {}),
          }
        : {}),
      columns: repeatingListColumns,
      // rowCount is the builder's STARTING count (default 2 there), not a cap —
      // AssessmentContent lets the member add further rows without limit. See
      // toInitialRowCount for the fallback/rounding rules. Ignored by
      // useRepeatingListRows.seedRows whenever initialRows is present below.
      initialRowCount: isRepeatingList ? toInitialRowCount(element.rowCount) : undefined,
      // Server-supplied starting row VALUES, sanitized per toInitialRows'
      // doc comment. `undefined` (never `[]`) when there's nothing usable, so
      // seedRows' `initialRows && initialRows.length > 0` falls back to
      // initialRowCount blank rows unchanged.
      initialRows: isRepeatingList
        ? toInitialRows(
            prefilledAnswers?.[element.name] ?? prefilledAnswers?.[camelize(element.name)],
            repeatingListColumns ?? []
          )
        : undefined,
    };
  });
}
